import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { API_URL } from '../config/api';

interface LoginCredentials {
  username: string;
  password: string;
}

interface RegisterCredentials extends LoginCredentials {
  email: string;
}

interface User {
  id: string;
  username: string;
  email: string;
}

interface TokenResponse {
  access: string;
  refresh: string;
}

const loginApi = async (credentials: LoginCredentials): Promise<{ user: User; tokens: TokenResponse }> => {
  const tokenResponse = await fetch(`${API_URL}/auth/jwt/create/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(credentials),
  });

  if (!tokenResponse.ok) {
    const error = await tokenResponse.json();
    throw new Error(error.detail || 'Login failed');
  }

  const tokens = await tokenResponse.json();

  // Get user details with the new access token
  const userResponse = await fetch(`${API_URL}/auth/users/me/`, {
    headers: {
      'Authorization': `JWT ${tokens.access}`,
      'Content-Type': 'application/json',
    },
  });

  if (!userResponse.ok) {
    throw new Error('Failed to fetch user details');
  }

  const user = await userResponse.json();
  return { user, tokens };
};

const registerApi = async (credentials: RegisterCredentials): Promise<{ user: User; tokens: TokenResponse }> => {
  const registerResponse = await fetch(`${API_URL}/auth/users/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(credentials),
  });

  if (!registerResponse.ok) {
    const error = await registerResponse.json();
    const errorMessage = Object.entries(error)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    throw new Error(errorMessage || 'Registration failed');
  }

  // After successful registration, login with the same credentials
  return loginApi({
    username: credentials.username,
    password: credentials.password,
  });
};

export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { login: setAuth, logout: clearAuth, user, isAuthenticated, completeOnboarding } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      setAuth(data.user, data.tokens.access, data.tokens.refresh);
      // Invalidate and refetch any user-related queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      navigate('/dashboard');
    },
  });

  const registerMutation = useMutation({
    mutationFn: registerApi,
    onSuccess: (data) => {
      setAuth(data.user, data.tokens.access, data.tokens.refresh);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      navigate('/dashboard');
    },
  });

  const logout = () => {
    clearAuth();
    // Clear all queries from the cache
    queryClient.clear();
    navigate('/login');
  };

  return {
    user,
    isAuthenticated,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout,
    completeOnboarding,
    isLoading: loginMutation.isPending || registerMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
}; 